// frontend/src/socket.js
import { io } from 'socket.io-client';
import clientLogger from './services/clientLogger';

// Функція для визначення найкращого URL для підключення
function getSocketURL() {
    const hostname = window.location.hostname;
    const port = 10000;
    
    // Якщо працюємо в продакшені
    if (process.env.NODE_ENV === 'production') {
        return 'https://vibio-h5ma.onrender.com'; // Використовуємо хостинг Render
    }
    
    // Для розробки - динамічне визначення IP
    console.log('🌐 Detecting connection environment:');
    console.log('  - Current hostname:', hostname);
    console.log('  - Current location:', window.location.href);
    
    // Якщо це localhost/127.0.0.1, може бути проблема на мобільних
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        console.warn('⚠️  Warning: Using localhost - won\'t work on mobile devices!');
        console.log('💡 To test on mobile: use your computer\'s IP address instead');
        
        // Спробуємо отримати локальний IP (не завжди працює через безпеку браузера)
        try {
            // Це може не працювати в усіх браузерах
            console.log('🔍 Tip: Find your computer\'s IP with: ipconfig (Windows) or ifconfig (Mac/Linux)');
        } catch (error) {
            console.log('🔍 Can\'t auto-detect IP, use manual IP address for mobile testing');
        }
    }
    
    const socketURL = `http://${hostname}:${port}`;
    console.log('🔌 Socket.IO will connect to:', socketURL);
    
    return socketURL;
}

const SOCKET_URL = getSocketURL();

// Покращена конфігурація Socket.IO клієнта для стабільності
const socketOptions = {
    autoConnect: false,
    // Покращені таймаути для стабільності підключення
    timeout: 30000, // Збільшуємо таймаут підключення до 30сек
    
    // Автоматичне переподключення з експоненційною затримкою
    reconnection: true,
    reconnectionAttempts: 10, // Збільшуємо кількість спроб
    reconnectionDelay: 2000, // Початкова затримка 2сек
    reconnectionDelayMax: 10000, // Максимальна затримка 10сек
    randomizationFactor: 0.5,
    
    // Транспорти в порядку пріоритету для кращої сумісності
    transports: ['websocket', 'polling'],
    
    // Покращена обробка таймаутів
    upgrade: true,
    rememberUpgrade: true,
    forceNew: false, // НЕ створювати нове підключення якщо існує
    
    // Query параметри для діагностики
    query: {
        source: 'vibio-frontend',
        version: '2.0',
        timestamp: Date.now()
    }
};

export const socket = io(SOCKET_URL, socketOptions);

// Розширене логування для діагностики
socket.on('connect', () => {
    console.log('✅ Socket.IO connected successfully!');
    console.log('🆔 Connection ID:', socket.id);
    console.log('🚀 Transport:', socket.io.engine.transport.name);
    try { clientLogger.info('socket connected', { id: socket.id, transport: socket.io.engine.transport.name }, 'SOCKET'); } catch {}
    
    // Логуємо зміни транспорту
    socket.io.engine.on('upgrade', () => {
        console.log('⬆️ Transport upgraded to:', socket.io.engine.transport.name);
    });
});

socket.on('disconnect', (reason) => {
    console.log('❌ Socket.IO disconnected:', reason);
    try { clientLogger.warn('socket disconnected', { reason }, 'SOCKET'); } catch {}
    if (reason === 'io server disconnect') {
        console.log('🔄 Server disconnected the client, reconnecting...');
        socket.connect();
    }
});

socket.on('connect_error', (error) => {
    console.error('🚫 Socket.IO connection error:', error);
    console.error('🎯 Attempted URL:', SOCKET_URL);
    try { clientLogger.error('socket connect_error', { message: error.message, url: SOCKET_URL }, 'SOCKET'); } catch {}
    
    // Додаткові поради для виправлення
    if (error.message.includes('ERR_NETWORK') || error.message.includes('ECONNREFUSED')) {
        console.log('🔧 Possible solutions:');
        console.log('  1. Make sure backend server is running on port 10000');
        console.log('  2. Check firewall settings');
        console.log('  3. Verify the IP address is correct for mobile devices');
        console.log('  4. Try accessing:', `${SOCKET_URL}/health`);
    }
});

socket.on('reconnect', (attemptNumber) => {
    console.log(`🔄 Socket.IO reconnected after ${attemptNumber} attempts`);
    try { clientLogger.info('socket reconnect', { attemptNumber }, 'SOCKET'); } catch {}
});

socket.on('reconnect_attempt', (attemptNumber) => {
    console.log(`🔄 Socket.IO reconnection attempt #${attemptNumber}`);
    try { clientLogger.info('socket reconnect_attempt', { attemptNumber }, 'SOCKET'); } catch {}
});

socket.on('reconnect_error', (error) => {
    console.error('🚫 Socket.IO reconnection error:', error);
    try { clientLogger.error('socket reconnect_error', { message: error.message }, 'SOCKET'); } catch {}
});

socket.on('reconnect_failed', () => {
    console.error('💥 Socket.IO failed to reconnect after maximum attempts');
    console.log('🔧 Please check your connection and refresh the page');
    try { clientLogger.error('socket reconnect_failed', {}, 'SOCKET'); } catch {}
});

// Додаткові діагностичні функції
export const diagnostics = {
    // Тестування підключення
    async testConnection() {
        console.log('🧪 Testing Socket.IO connection...');
        
        try {
            // Тест HTTP доступності
            const response = await fetch(`${SOCKET_URL.replace('ws://', 'http://').replace('wss://', 'https://')}/health`);
            if (response.ok) {
                console.log('✅ HTTP endpoint accessible');
                const data = await response.text();
                console.log('📄 Health check response:', data);
            } else {
                console.log('❌ HTTP endpoint not accessible:', response.status);
            }
        } catch (error) {
            console.error('❌ HTTP test failed:', error.message);
        }
        
        // Тест Socket.IO підключення
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                console.log('⏰ Connection test timeout');
                resolve(false);
            }, 10000);
            
            socket.once('connect', () => {
                console.log('✅ Socket.IO connection test successful');
                clearTimeout(timeout);
                resolve(true);
            });
            
            socket.once('connect_error', (error) => {
                console.log('❌ Socket.IO connection test failed:', error.message);
                clearTimeout(timeout);
                resolve(false);
            });
            
            socket.connect();
        });
    },
    
    // Отримання інформації про мережу
    getNetworkInfo() {
        const info = {
            url: SOCKET_URL,
            hostname: window.location.hostname,
            protocol: window.location.protocol,
            port: window.location.port,
            userAgent: navigator.userAgent,
            online: navigator.onLine,
            connectionType: navigator.connection?.effectiveType || 'unknown'
        };
        
        console.table(info);
        return info;
    }
};

// Глобальні слухачі для додаткової діагностики (можна включити для дебагу)
if (process.env.NODE_ENV === 'development') {
    socket.onAny((event, ...args) => {
        // Розкоментуйте для детального логування всіх Socket.IO подій
        // console.log(`🔊 Socket event: ${event}`, args);
    });
}
